import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Upload from 'lucide-react/dist/esm/icons/upload';
import Pencil from 'lucide-react/dist/esm/icons/pencil';
import Eye from 'lucide-react/dist/esm/icons/eye';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import X from 'lucide-react/dist/esm/icons/x';
import { useAmlStore } from '../../stores/amlStore';
import { amlApi } from '../../services/amlApi';
import type { AmlTransaction } from '../../types/aml';
import TransactionFilter from './TransactionFilter';
import TransactionForm from './TransactionForm';
import TransactionImportForm from './TransactionImportForm';
import { PageSizeSelect, SortableHeader, TableEmptyRow, InlineCell, formatMoney, formatDate } from '../shared';
import type { InlineCellType } from '../shared';
import { Button, Badge, Checkbox, ConfirmationDialog, IconButton, Spinner, Text, Tooltip, Pagination } from '../../../../components/ui';
import { useToast } from '../../../../components/ui';
import { usePermissions, Permissions } from '../../../../hooks/usePermissions';
import { useTranslation } from '../../../../hooks';
import { useUIStore } from '../../../../stores/uiStore';
import { clearLegacyImportDrafts } from '../../utils/importDraft';

interface ColumnBase {
  field: keyof AmlTransaction;
  label: string;
  width: string;
  align: 'left' | 'right';
  format: (t: AmlTransaction) => React.ReactNode;
  sortField: string;
  truncate?: boolean;
  muted?: boolean;
}

type ColumnDef =
  | (ColumnBase & { editable: true; type: InlineCellType })
  | (ColumnBase & { editable: false });


const TransactionList: React.FC = () => {
  const {
    transactions,
    transactionsTotal,
    transactionsPage,
    transactionsPageSize,
    transactionsSort,
    transactionsSortDir,
    transactionFilters,
    selectedTransaction,
    loading,
    transactionsLoading,
    showForm,
    showImport,
    fetchTransactions,
    selectTransaction,
    setShowForm,
    setShowImport,
    setTransactionsPageSize,
    setTransactionsSort,
    deleteTransaction,
    bulkDeleteTransactions,
  } = useAmlStore(
    useShallow((s) => ({
      transactions: s.transactions,
      transactionsTotal: s.transactionsTotal,
      transactionsPage: s.transactionsPage,
      transactionsPageSize: s.transactionsPageSize,
      transactionsSort: s.transactionsSort,
      transactionsSortDir: s.transactionsSortDir,
      transactionFilters: s.transactionFilters,
      selectedTransaction: s.selectedTransaction,
      loading: s.loading,
      transactionsLoading: s.transactionsLoading,
      showForm: s.showForm,
      showImport: s.showImport,
      fetchTransactions: s.fetchTransactions,
      selectTransaction: s.selectTransaction,
      setShowForm: s.setShowForm,
      setShowImport: s.setShowImport,
      setTransactionsPageSize: s.setTransactionsPageSize,
      setTransactionsSort: s.setTransactionsSort,
      deleteTransaction: s.deleteTransaction,
      bulkDeleteTransactions: s.bulkDeleteTransactions,
    })),
  );

  const { language } = useUIStore();
  const { t: tr } = useTranslation(language);
  const [hasSavedImport, setHasSavedImport] = useState(false);

  useEffect(() => {
    clearLegacyImportDrafts();
  }, []);

  useEffect(() => {
    if (showImport) return;
    let cancelled = false;
    amlApi.getImportDraftMeta()
      .then((meta) => { if (!cancelled) setHasSavedImport(!!meta.exists); })
      .catch(() => { if (!cancelled) setHasSavedImport(false); });
    return () => { cancelled = true; };
  }, [showImport]);

  const COLUMNS: ColumnDef[] = useMemo(() => [
    {
      field: 'transaction_date', label: tr('amlColDate'), width: '140px', editable: true,
      type: 'date', align: 'left', sortField: 'transaction_date',
      format: (t) => formatDate(t.transaction_date),
    },
    {
      field: 'customer_name', label: tr('amlColCustomerLegalName'), width: '180px', editable: false,
      align: 'left', sortField: 'customer_name', truncate: true,
      format: (t) => t.customer_name ?? '—',
    },
    {
      field: 'location_name', label: tr('amlColLocation'), width: '160px', editable: false,
      align: 'left', sortField: 'location_name', truncate: true,
      format: (t) => t.location_name ?? '—',
    },
    {
      field: 'inbound', label: tr('amlColInbound'), width: '110px', editable: true,
      type: 'number', align: 'right', sortField: 'inbound',
      format: (t) => formatMoney(t.inbound),
    },
    {
      field: 'outbound', label: tr('amlColOutbound'), width: '110px', editable: true,
      type: 'number', align: 'right', sortField: 'outbound',
      format: (t) => formatMoney(t.outbound),
    },
    {
      field: 'amount_collected', label: tr('amlColAmount'), width: '150px', editable: false,
      align: 'right', sortField: 'amount_collected', muted: true,
      format: (t) => formatMoney(t.amount_collected),
    },
    {
      field: 'fee_inbound', label: tr('amlColFeeInbound'), width: '110px', editable: true,
      type: 'number', align: 'right', sortField: 'fee_inbound',
      format: (t) => formatMoney(t.fee_inbound),
    },
    {
      field: 'fee_outbound', label: tr('amlColFeeOutbound'), width: '110px', editable: true,
      type: 'number', align: 'right', sortField: 'fee_outbound',
      format: (t) => formatMoney(t.fee_outbound),
    },
    {
      field: 'fee_collected', label: tr('amlColAmountFee'), width: '120px', editable: false,
      align: 'right', sortField: 'fee_collected', muted: true,
      format: (t) => formatMoney(t.fee_collected),
    },
    {
      field: 'invoice_number', label: tr('amlColInvoiceNumber'), width: '120px', editable: true,
      type: 'text', align: 'left', sortField: 'invoice_number',
      format: (t) => t.invoice_number ?? '—',
    },
    {
      field: 'invoice_date', label: tr('amlColInvoiceDate'), width: '140px', editable: true,
      type: 'date', align: 'left', sortField: 'invoice_date',
      format: (t) => formatDate(t.invoice_date),
    },
    {
      field: 'invoice_amount', label: tr('amlColInvoiceAmount'), width: '120px', editable: true,
      type: 'number', align: 'right', sortField: 'invoice_amount',
      format: (t) => formatMoney(t.invoice_amount),
    },
    {
      field: 'stops', label: tr('amlColStops'), width: '70px', editable: true,
      type: 'number', align: 'right', sortField: 'stops',
      format: (t) => t.stops ?? '—',
    },
    {
      field: 'pcs', label: tr('amlColPcs'), width: '70px', editable: true,
      type: 'number', align: 'right', sortField: 'pcs',
      format: (t) => t.pcs ?? '—',
    },
    {
      field: 'notes', label: tr('amlColNotes'), width: '140px', editable: true,
      type: 'textarea', align: 'left', sortField: 'notes', truncate: true,
      format: (t) => t.notes ?? '—',
    },
  ], [tr]);

  const mountedRef = useRef(false);
  const [initialised, setInitialised] = useState(false);
  const { success, error } = useToast();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permissions.AML_TRANSACTIONS_MANAGE);
  const [deleteTarget, setDeleteTarget] = useState<AmlTransaction | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const headerCheckRef = useRef<HTMLInputElement>(null);

  const handleEdit = useCallback((e: React.MouseEvent, t: AmlTransaction) => {
    e.stopPropagation();
    selectTransaction(t);
    setShowForm('transaction-edit');
  }, [selectTransaction, setShowForm]);

  const handleView = useCallback((e: React.MouseEvent, t: AmlTransaction) => {
    e.stopPropagation();
    selectTransaction(t);
    setShowForm('transaction-view');
  }, [selectTransaction, setShowForm]);

  const handleDeleteClick = useCallback((e: React.MouseEvent, t: AmlTransaction) => {
    e.stopPropagation();
    setDeleteTarget(t);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteTransaction(deleteTarget.id);
      success(tr('amlTransactionToastDeleted'));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(deleteTarget.id);
        return next;
      });
      setDeleteTarget(null);
    } catch {
      error(tr('amlTransactionToastFailedToDelete'));
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleteTransaction, success, error, tr]);

  const pageIds = useMemo(() => transactions.map((t) => t.id), [transactions]);
  const selectedCount = selectedIds.size;
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [transactionFilters, transactionsSort, transactionsSortDir, transactionsPageSize, transactionsPage]);

  const toggleRow = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const togglePage = useCallback((checked: boolean) => {
    setSelectedIds(checked ? new Set(pageIds) : new Set());
  }, [pageIds]);

  const handleClearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handlePageChange = useCallback((p: number) => {
    setSelectedIds(new Set());
    fetchTransactions(p);
  }, [fetchTransactions]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const deleted = await bulkDeleteTransactions(Array.from(selectedIds).slice(0, 500));
      success(tr('amlTransactionToastBulkDeleted', { count: deleted }));
      setSelectedIds(new Set());
      setBulkConfirmOpen(false);
    } catch {
      error(tr('amlTransactionToastFailedToDelete'));
    } finally {
      setDeleting(false);
    }
  }, [selectedIds, bulkDeleteTransactions, success, error, tr]);

  const patchField = useCallback(async (t: AmlTransaction, field: keyof AmlTransaction, raw: string) => {
    const value = raw === '' ? null : raw;
    const updated = await amlApi.updateTransaction(t.id, { [field]: value });
    const { transactions: current } = useAmlStore.getState();
    useAmlStore.setState({
      transactions: current.map((row) => row.id === t.id ? { ...row, ...updated } : row),
    });
    success(tr('amlTransactionToastSaved'));
  }, [success, tr]);

  useEffect(() => {
    fetchTransactions().finally(() => setInitialised(true));
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, [fetchTransactions]);

  useEffect(() => {
    if (!mountedRef.current) return;
    fetchTransactions(1);
  }, [transactionsSort, transactionsSortDir, transactionsPageSize, fetchTransactions]);

  const totalPages = Math.max(1, Math.ceil(transactionsTotal / transactionsPageSize));

  const showTransactionForm = showForm === 'transaction-create' || showForm === 'transaction-edit' || showForm === 'transaction-view';
  const formTransaction = showForm !== 'transaction-create' ? selectedTransaction : null;

  const handleOpenCreate = useCallback(() => setShowForm('transaction-create'), [setShowForm]);
  const handleOpenImport = useCallback(() => setShowImport(true), [setShowImport]);
  const handleCloseForm = useCallback(() => setShowForm(null), [setShowForm]);
  const handleCloseImport = useCallback(() => setShowImport(false), [setShowImport]);
  const handleCloseDelete = useCallback(() => setDeleteTarget(null), []);
  const handleCloseBulkConfirm = useCallback(() => setBulkConfirmOpen(false), []);
  const handleOpenBulkConfirm = useCallback(() => setBulkConfirmOpen(true), []);

  const renderCell = useCallback((col: ColumnDef, t: AmlTransaction) => {
    const content = col.format(t);
    const isRight = col.align === 'right';
    const baseDisplay = col.truncate ? (
      <Tooltip content={String(content)} placement="top" onlyWhenTruncated>
        <span className="block truncate">{content}</span>
      </Tooltip>
    ) : (
      <span className={isRight ? 'whitespace-nowrap' : ''}>{content}</span>
    );

    if (col.editable && canManage) {
      return (
        <InlineCell
          value={(t as any)[col.field] ?? null}
          type={col.type}
          display={baseDisplay}
          align={col.align}
          onSave={(v) => patchField(t, col.field, v)}
        />
      );
    }

    return baseDisplay;
  }, [canManage, patchField]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <TransactionFilter />
        {canManage && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="lg" leftIcon={<Upload size={14} />} onClick={handleOpenImport}>
              {tr('amlBtnImport')}
              {hasSavedImport ? (
                <Badge variant="info" size="sm" className="ml-1.5">{tr('amlImportSavedBadge')}</Badge>
              ) : null}
            </Button>
            <Button variant="success" size="lg" leftIcon={<Plus size={14} />} onClick={handleOpenCreate}>
              {tr('amlBtnAddTransaction')}
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-subtle bg-surface-primary overflow-hidden">
        {!initialised || (loading && transactions.length === 0) ? (
          <div className="flex items-center justify-center h-48 text-content-muted">
            <Spinner size="lg" />
          </div>
        ) : transactions.length === 0 ? (
          <TableEmptyRow title={tr('amlTableNoTransactionsFound')} />
        ) : (
          <div
            className={`overflow-x-auto${transactionsLoading ? ' opacity-60 pointer-events-none' : ''}`}
            style={{ transition: 'opacity 0.15s ease' }}
          >
            <table className="text-sm" style={{ minWidth: '1600px', width: '100%', tableLayout: 'fixed' }}>
              <colgroup>
                {canManage ? <col style={{ width: '44px' }} /> : null}
                <col style={{ width: '120px' }} />
                {COLUMNS.map((c) => (
                  <col key={c.field} style={{ width: c.width }} />
                ))}
              </colgroup>
              <thead>
                <tr className="border-b border-subtle bg-surface-secondary/50">
                  {canManage ? (
                    <th className="px-3 py-3 text-left" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        ref={headerCheckRef}
                        checked={allPageSelected}
                        onChange={togglePage}
                        aria-label={tr('amlTransactionAriaLabelSelectAll')}
                      />
                    </th>
                  ) : null}
                  <th className="px-4 py-3 text-left" />
                  {COLUMNS.map((c) => (
                    <SortableHeader
                      key={c.field}
                      label={c.label}
                      field={c.sortField}
                      currentSort={transactionsSort}
                      currentDir={transactionsSortDir}
                      onSort={setTransactionsSort}
                      align={c.align}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {transactions.map((t: AmlTransaction) => (
                  <tr
                    key={t.id}
                    onClick={() => selectTransaction(t)}
                    className={`
                      border-b border-subtle cursor-pointer transition-colors
                      ${selectedIds.has(t.id) ? 'bg-brand/10' : selectedTransaction?.id === t.id ? 'bg-brand/10' : 'hover:bg-surface-secondary'}
                    `}
                  >
                    {canManage ? (
                      <td className="px-3 py-2 h-11" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(t.id)}
                          onChange={(checked) => toggleRow(t.id, checked)}
                          aria-label={tr('amlTransactionAriaLabelSelect')}
                        />
                      </td>
                    ) : null}
                    <td className="px-4 py-2 h-11 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-start">
                        {canManage && (
                          <Tooltip content={tr('amlTransactionTooltipFullEdit')} placement="top">
                            <IconButton
                              icon={<Pencil size={14} />}
                              variant="ghost"
                              aria-label={tr('amlTransactionAriaLabelEdit')}
                              onClick={(e) => handleEdit(e, t)}
                            />
                          </Tooltip>
                        )}
                        <Tooltip content={tr('amlTransactionTooltipView')} placement="top">
                          <IconButton
                            icon={<Eye size={14} />}
                            variant="ghost"
                            aria-label={tr('amlTransactionAriaLabelView')}
                            onClick={(e) => handleView(e, t)}
                          />
                        </Tooltip>
                        {canManage && (
                          <Tooltip content={tr('amlTransactionTooltipDelete')} placement="top">
                            <IconButton
                              icon={<Trash2 size={14} />}
                              variant="ghost"
                              aria-label={tr('amlTransactionAriaLabelDelete')}
                              onClick={(e) => handleDeleteClick(e, t)}
                              className="text-status-error hover:text-status-error"
                            />
                          </Tooltip>
                        )}
                      </div>
                    </td>
                    {COLUMNS.map((col) => {
                      const isRight = col.align === 'right';
                      const muted = !col.editable && col.muted;
                      return (
                        <td
                          key={col.field}
                          className={`
                            px-4 py-2 h-11
                            ${isRight ? 'text-right' : ''}
                            ${col.truncate ? 'overflow-hidden max-w-0' : 'whitespace-nowrap'}
                            ${muted ? 'text-content-secondary' : 'text-content'}
                          `}
                        >
                          {renderCell(col, t)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canManage && selectedCount > 0 ? (
        <div className="fixed bottom-16 inset-x-0 z-sticky flex justify-center pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-3 px-3 py-2 rounded-xl border border-default bg-surface-glass-strong backdrop-blur-md shadow-lg animate-fade-in">
          <Text variant="caption">{tr('amlTransactionBulkSelected', { count: selectedCount })}</Text>
          <Button
            variant="danger"
            size="sm"
            leftIcon={<Trash2 size={14} />}
            onClick={handleOpenBulkConfirm}
            disabled={deleting}
          >
            {tr('amlTransactionBulkDelete')}
          </Button>
          <IconButton
            icon={<X size={14} />}
            variant="ghost"
            aria-label={tr('amlTransactionAriaLabelClearSelection')}
            onClick={handleClearSelection}
          />
          </div>
        </div>
      ) : null}

      {transactions.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 rounded-xl border border-subtle bg-surface-primary">
          <PageSizeSelect value={transactionsPageSize} onChange={setTransactionsPageSize} />
          <Pagination
            page={transactionsPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={transactionsTotal}
            startIndex={(transactionsPage - 1) * transactionsPageSize}
            endIndex={Math.min(transactionsPage * transactionsPageSize, transactionsTotal)}
          />
        </div>
      )}

      {showTransactionForm && (
        <TransactionForm
          transaction={formTransaction}
          onClose={handleCloseForm}
          readOnly={showForm === 'transaction-view'}
        />
      )}
      {showImport && (
        <TransactionImportForm onClose={handleCloseImport} />
      )}

      {deleteTarget && (
        <ConfirmationDialog
          isOpen={!!deleteTarget}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title={tr('amlTransactionDeleteTitle')}
          message={tr('amlTransactionDeleteMessage')}
          confirmText={tr('amlTransactionDeleteConfirm')}
          variant="danger"
        />
      )}

      {bulkConfirmOpen && (
        <ConfirmationDialog
          isOpen={bulkConfirmOpen}
          onClose={handleCloseBulkConfirm}
          onConfirm={handleBulkDeleteConfirm}
          title={tr('amlTransactionBulkDeleteTitle')}
          message={tr('amlTransactionBulkDeleteMessage', { count: selectedCount })}
          confirmText={tr('amlTransactionDeleteConfirm')}
          variant="danger"
        />
      )}
    </div>
  );
};

export default TransactionList;
